import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Alert, Button, Card, Col, DatePicker, Divider, Input, InputNumber, message, Row, Select, Steps, Tag, Typography, Upload } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";

import { CreditCardOutlined, UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { API_URL, LOGGED_USER, TOKEN } from "../../../../variables/constants";
import axiosInstance from "../../../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../../../variables/api/axiosMultiPartDatainstance";
import useEnginReferentiels from "../../../../hooks/useEnginReferentiels";
import { MarqueSelect, TypeAutoComplete } from "../../../../components/engin/MarqueTypeFields";
import { coerceToCategorie } from "../../../../components/engin/categorieRules";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import { rightsService } from "../../../../services/rightsService";

const initPersonne = {
    nom: "",
    prenom: "",
    genre: "",
    profession: "",
    contact: [],
    typePersonne: "",
    adresse: "",
    pieces: []
};

const ChangementDeVehicule = (props) => {
    const [loading, setLoading] = useState();
    const [contrat, setContrat] = useState();
    const [typePieces, setTypePieces] = useState([]);
    const [selectedEngin, setSelectedEngin] = useState();
    const [modalAssureOpen, setModalAssureOpen] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);
    const [errors, setErrors] = useState([{}]);
    const [existChassis, setExistChassis] = useState([false]);
    const [existImmatriculation, setExistImmatriculation] = useState([false]);
    const [concessionnaires, setConcessionnaires] = useState([]);
    const [garanties, setGaranties] = useState([[]]);
    const [ncGuaranties, setNcGuaranties] = useState([]);
    const [carteGrise, setCarteGrise] = useState("");
    const [showAddPersonModal, setShowAddPersonModal] = useState(false);
    const [personneId, setPersonneId] = useState(0); // id de l'assuré suivant le numéro de l'engin dans la flotte
    const [professions, setProfessions] = useState([]);
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });
    // Preview financier (calculé côté backend) — évite les champs à null au frontend
    const [preview, setPreview] = useState(null);

    // Date d'effet du CV (pilote le prorata) — défaut : aujourd'hui.
    const [dateEffet, setDateEffet] = useState(dayjs());
    // BNS édité par l'utilisateur (null = on garde le BNS repris du contrat, renvoyé par le preview).
    const [bonusMalusEdite, setBonusMalusEdite] = useState(null);
    // Édition du bonus/malus réservée à l'habilitation HCO080 (comme au Renouvellement).
    const canEditBonus = rightsService("HCO080");
    // Frais accessoires édités (null = barème par défaut renvoyé par le preview).
    const [accessoiresEdites, setAccessoiresEdites] = useState(null);
    // Édition des frais accessoires réservée à l'habilitation HCO079 (comme au Renouvellement).
    const canEditAccessoires = rightsService("HCO079");
    // Étape du formulaire : 0 = saisie, 1 = confirmation récapitulative.
    const [currentStep, setCurrentStep] = useState(0);

    const [modalChangementVehicule, setModalChangementVehicule] = useState(
        props.open
    );

    const [formValues, setFormValues] = useState([
        {
            categorie: contrat?.engins[0]?.categorie,
            marque: "",
            numeroSerie: "",
            matricule: "",
            typeSerie: "TG",
            immatriculation: "",
            puissance: "",
            volume: "",
            nombrePlace: 0,
            energie: "ESSENCE",
            numeroChassis: "",
            valeurDeclaree: "",
            valeurVenale: "",
            valeurANeuf: "",
            dateMiseEnCirculation: "",
            puissanceType: [],
            sousGaranties: [],
            garanties: [],
            usage: "TAXI",
            carosserie: null,
            moteur: "",
            type: "",
            // Pas de défaut MOTO en dur : null hors moto (le genre du nouvel engin suit la
            // catégorie du contrat ; select requis pour une moto).
            genre: null,
            concessionnaire: null,
            rc: 0,
            carteGrise: "",
            personne: {
                personneId: 0,
                nom: "",
                prenom: "",
                contact: [],
                profession: "",
                genre: "",
                adresse: "",
                email: "",
                pieces: [],
                piece: {
                    numIdentite: "",
                    lieuDelivrance: "",
                    dateDelivrance: "",
                    typePiece: ""
                }
            },
            codeId: "0"
        },
    ]);

    const handleDateMiseEnCirculation = (value, index) => {
        let newFormValues = [...formValues];
        newFormValues[index]["dateMiseEnCirculation"] = value.toISOString();
        // sessionStorage.setItem("formValues", JSON.stringify(newFormValues));
        setFormValues(newFormValues);
    };

    useEffect(() => {
        setModalChangementVehicule(props.open);
        setContrat(props.contrat);
        // Réinitialiser le flux à chaque ouverture : on repart de la saisie (étape 0) avec la
        // date d'effet du jour et le BNS repris (non édité).
        if (props.open) {
            setCurrentStep(0);
            setDateEffet(dayjs());
            setBonusMalusEdite(null);
        }
    }, [props.open]);

    // Valeur par défaut = valeur vénale MINIMALE de la catégorie du contrat, tant que
    // l'utilisateur n'a pas saisi de valeur (ni déclarée, ni à neuf) pour le nouvel engin.
    // Le nouveau véhicule reste dans la même catégorie (le CV ne change que la puissance),
    // donc la valeur plancher tarifaire à afficher est celle de cette catégorie.
    useEffect(() => {
        const venaleMin = contrat?.engins?.[0]?.categorie?.valeurVenaleMinimale;
        if (!venaleMin) return;
        setFormValues((prev) => {
            const fv = prev[0] || {};
            const vide = (v) => v === "" || v === null || v === undefined;
            if (!vide(fv.valeurDeclaree) || !vide(fv.valeurANeuf) || !vide(fv.valeurVenale)) {
                return prev;
            }
            const next = [...prev];
            next[0] = {
                ...fv,
                valeurVenale: venaleMin,
                valeurANeuf: venaleMin,
                valeurDeclaree: venaleMin,
            };
            return next;
        });
    }, [contrat]);

    const param = useParams();
    const navigate = useNavigate();

    const prop = (index) => {
        return {
            beforeUpload: (file) => {
                // console.log("Arranger foufou: ", file);
                if (file.type !== "application/pdf") {
                    message.error("Vous ne pouvez uploader que des fichiers PDF!");
                    return false;
                } else {
                    var cg = [];
                    cg.push(file);
                    setCarteGrise(cg);
                    uploadGrayCard(file, index);
                    return false;
                }
            },
            showUploadList: true,
            maxCount: 1,
            data: carteGrise,
            onChange(info) {
                if (info.file.status === "done") {
                    message.success(`${info.file.name} carte grise uploadée avec succès`);
                } else if (info.file.status === "error") {
                    message.error(`${info.file.name} carte grise non uploadée!`);
                }
                console.log("Info: ", info);
            }
        };
    };

    const uploadGrayCard = async (e, index) => {
        const formData = new FormData();

        // console.log("Fillllllllllles: ", e);

        let extension = e.name.substring(
            e.name.lastIndexOf(".") + 1,
            e.name.length
        );

        let d = new Date();
        let filename = `carteGrise_${d?.toISOString()?.split(" ").join("-").split(":").join("-").split(".")[0]
            }.${extension}`;
        console.log("d: ", d, " fn: ", filename);
        formData.append(`fichier`, e, filename);

        axiosMultiPartDataInstance
            .post(`/api/engin/carte-grise`, formData)
            .then((response) => {
                console.log(response.data);
                // let newFormValues = [...formData];
                // // newFormValues[index]["carteGrise"] = response.data;
                // setFormData(newFormValues);
                handleChange(index, "carteGrise", response.data);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    useEffect(() => {
        axiosInstance
            .get(`/api/type-piece`)
            .then((res) => {
                // console.log(res.data);
                setTypePieces(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                const sorted = [...(res.data || [])].sort((a, b) =>
                    (a.libelleParametre || "").localeCompare(
                        b.libelleParametre || "",
                        "fr",
                        { sensitivity: "base" }
                    )
                );
                setProfessions(sorted);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get(`/api/organisme/concessionnaire`)
            .then((res) => {
                let dt = [];
                res.data.forEach((org) => {
                    dt.push({
                        label: org.raisonSociale,
                        value: JSON.stringify(org)
                    });
                });
                setConcessionnaires(dt);
            })
            .catch((err) => {
                console.log(err);
            });

    }, [TOKEN]);

    // Référentiels engin (source unique partagée) : genre/carrosserie filtrés par la CATÉGORIE
    // du contrat avec repli global + dédup, et marques (/api/vehicule-marques) qui portent les
    // MODÈLES alimentant l'AutoComplete du champ « Type ».
    const catCode = contrat?.engins?.[0]?.categorie?.codeCategorie?.trim();
    const { genres, carosseries, marques } = useEnginReferentiels(catCode);

    // Genre/carrosserie hérités mais absents de la liste de la catégorie → élément par défaut.
    useEffect(() => {
        if (!genres.length && !carosseries.length) return;
        setFormValues((prev) => {
            let changed = false;
            const next = (prev || []).map((v) => {
                const genre = coerceToCategorie(v?.genre, genres);
                const carosserie = coerceToCategorie(v?.carosserie, carosseries);
                if (genre === v?.genre && carosserie === v?.carosserie) return v;
                changed = true;
                return { ...v, genre, carosserie };
            });
            return changed ? next : prev;
        });
    }, [genres, carosseries]);

    const someEffects = (codeId) => {
        var dt = [[]];
        dt[codeId] = [];
        let newFormValues = [...formValues];
        var categorieId = contrat?.engins[0]?.categorie?.categorieId;

        axiosInstance
            .get(`/api/garantie/categorie/compulsory/${categorieId}`)
            .then((res) => {
                // console.log("Guaranties", res.data);
                res.data?.forEach((guaranty, index) => {
                    axiosInstance
                        // codeId (string « PO… ») → variante /codeId/ ; /capital/{id} attend un Integer (→ 400).
                        .get(`/api/sous-garantie/capital/codeId/${guaranty.codeId}`)
                        .then((response) => {
                            // console.log("Responses...", response.data);
                            dt[codeId].push({
                                key: index + 1,
                                nom: `${guaranty.nom} - ${guaranty.categorie.nom}`,
                                capital: guaranty.nom.toLowerCase().startsWith("rc")
                                    ? "-"
                                    : response.data[0],
                                primeNette: guaranty.nom.toLowerCase().startsWith("rc")
                                    ? newFormValues[codeId].rc
                                    : response.data[1]
                            });
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                });

                setTimeout(() => {
                    // console.log("SubGuaranties", dt);
                    var dts = [];
                    dt.forEach((sg) => {
                        sg.forEach((g) => {
                            dts.push(g);
                        });
                    });
                    sessionStorage.setItem("subGaranties", JSON.stringify(dts));

                    // console.log("DTS: ", dts);
                }, 1000);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            // L'endpoint exige l'organisme (compagnie du contrat) : /non-compulsory/{catId}/{orgCode}.
            // Sans lui → 404 (route non résolue). On scope sur la compagnie du contrat.
            .get(`/api/garantie/categorie/non-compulsory/${categorieId}/${contrat?.organisme?.code}`)
            .then((res) => {
                // console.log(res.data);
                sessionStorage.setItem("ncGuaranties", JSON.stringify(res.data));
            })
            .catch((err) => {
                console.log(err);
            });
        //
    };

    const handlePuissance = (value, codeId) => {
        var puissanceRC = value;
        var categorieId = contrat?.engins[0]?.categorie?.categorieId;
        axiosInstance
            .get(`/api/sous-garantie/rc/${puissanceRC}/${categorieId}`)
            .then((resp) => {
                // console.log(resp);
                let newFormValues = [...formValues];
                newFormValues[codeId]["rc"] = resp.data.primeNette;
                // sessionStorage.setItem("rcPN", resp.data.primeNette);
                sessionStorage.setItem("formValues", JSON.stringify(newFormValues));
                setFormValues(newFormValues);
                someEffects(codeId);
            })
            .catch((errp) => {
                console.log(errp);
            });
    };

    const handleChangementVehiculeSubmit = () => {
        if (selectedEngin) {
            const validated = validateForm();
            if (validated) {
                setLoading(true);
                // console.log("Current contrat: ", contrat);
                // console.log("FormValues: ", formValues);
                var allEngins = [];
                formValues[0].categorie = contrat.engins[0].categorie;
                if (formValues[0].personne.piece.numIdentite) {
                    var pieces = [];
                    pieces.push(formValues[0].piece);

                    var personne = initPersonne;
                    personne.nom = formValues[0].personne.nom;
                    personne.prenom = formValues[0].personne.prenom;
                    personne.genre = formValues[0].personne.genre;
                    personne.adresse = formValues[0].personne.adresse;
                    personne.profession = formValues[0].personne.profession;
                    personne.contact = formValues[0].personne.contact;
                    personne.typePersonne = "PHYSIQUE";

                    axiosInstance
                        .post(`/api/piece-identite/all`, JSON.stringify(pieces))
                        .then((res) => {
                            personne.pieces = res.data;
                            axiosInstance
                                .get(
                                    `/api/personne/code-assure-p/${LOGGED_USER.profil.organisme.organismeId}`)
                                .then((resCode) => {
                                    personne.numeroAssureP = resCode.data;
                                    axiosInstance
                                        .post(`/api/personne`, JSON.stringify(personne))
                                        .then((resPersonne) => {
                                            formValues[0].personne = resPersonne.data;
                                            allEngins.push(formValues[0]);
                                        })
                                        .catch((errPersonne) => {
                                            console.log(errPersonne);
                                        });
                                })
                                .catch((errCode) => {
                                    console.log(errCode);
                                });
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                } else {
                    formValues[0].personne = contrat.souscripteur;
                    allEngins.push(formValues[0]);
                }

                axiosInstance
                    .put(
                        `/api/engin/changement-vehicule/codeId/${contrat.codeId
                        }/${JSON.parse(selectedEngin).codeId}${buildCvQueryString()}`,
                        JSON.stringify(allEngins[0]))
                    .then(async (resEngin) => {
                        // console.log(resEngin.data);
                        await applyAvenantAgenceCommission(resEngin.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
                        toast("Changement de vehicule effectué!");
                        setLoading(false);
                        setModalChangementVehicule(false);
                        props.update(false);
                        // L'avenant CV crée une NOUVELLE version du contrat (nouveau codeId,
                        // l'ancienne est désactivée) : on navigue vers ce nouveau codeId pour
                        // que la page recharge les données à jour. Ne surtout pas retomber sur
                        // l'ancien codeId, sinon l'écran affiche la version périmée.
                        const newCodeId = resEngin.data?.codeId;
                        if (newCodeId && newCodeId !== contrat?.codeId) {
                            navigate(`/details-contrat/${newCodeId}`, { replace: true });
                        } else {
                            props.onRefresh?.();
                        }
                    })
                    .catch((errEngin) => {
                        console.log(errEngin);
                        toast("Erreur lors du changement du véhicule!");
                    });
            } else {
                toast("Entrez correctement les informations du vehicule");
            }
        } else {
            toast("Veuillez selectionner l'engin a changer!");
        }
    };

    const loadModal = (index) => {
        setModalIndex(index);
        setModalAssureOpen(true);
    };

    const handleShowAddPersonModal = (id) => {
        setPersonneId(id);
        setShowAddPersonModal(true);
    };

    const handleCancel = () => {
        setModalChangementVehicule(false);
        props.update(false);
    };

    const handleChange = (e, name, index) => {
        let newFormValues = [...formValues];

        if (name === "carteGrise") {
            newFormValues[index].carteGrise = e;
        } else {
            const { id } = e.target;
            // Champs texte libres saisis à la main : forcés en MAJUSCULES automatiquement
            // (immatriculation, moteur, n° de châssis). Cohérent avec la vérification
            // d'existence qui reçoit alors la même valeur normalisée.
            const CHAMPS_MAJUSCULES = ["immatriculation", "moteur", "numeroChassis"];
            const value = (CHAMPS_MAJUSCULES.includes(id) && typeof e.target.value === "string")
                ? e.target.value.toUpperCase()
                : e.target.value;
            newFormValues[index][id] = value;
            if (e.target.name === "valeurVenale") {
                newFormValues[index].valeurANeuf = value;
                newFormValues[index].valeurDeclaree = value;
            }

            // Immatriculation : on ne vérifie l'existence qu'à partir de 6 caractères saisis
            // (évite un appel réseau + un « déjà existante » à chaque frappe des premières lettres).
            if (id === "immatriculation") {
                if (value && value.trim().length >= 6) {
                    verifyImmatriculation(value.trim(), index);
                } else {
                    // En-dessous du seuil : on réinitialise l'éventuel drapeau « déjà existante ».
                    setExistImmatriculation((prev) => {
                        const ei = [...prev];
                        ei[index] = false;
                        return ei;
                    });
                }
            }
            // console.log("Here out", id);
            if (id === "numeroChassis") {
                // console.log("Here");
                verifyChassisNumber(value, index);
            }
        }

        setFormValues(newFormValues);
    };

    const verifyChassisNumber = (numChassis, index) => {
        // console.log("Verifying...");
        axiosInstance
            .get(`/api/engin/chassis/${numChassis}`)
            .then((res) => {
                let ec = [...existChassis];
                ec[index] = res.data;
                // console.log("chasss: ", numChassis, res.data);
                if (res.data) {
                    toast("Un moteur est déjà enregistré avec ce numéro de châssis");
                }
                setExistChassis(ec);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const verifyImmatriculation = (immatriculation, index) => {
        axiosInstance
            .get(`/api/engin/immatriculation/${immatriculation}`)
            .then((res) => {
                console.log("Immatriculation: ", immatriculation);
                console.log(res.data, index);
                let ei = [...existImmatriculation];
                ei[index] = res.data;
                if (res.data) {
                    toast("Immatriculation déjà existante!");
                }
                setExistImmatriculation(ei);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const validateForm = () => {
        const newErrors = [];

        formValues.forEach((fd, index) => {
            newErrors.push({});
            console.log("eiiim", existImmatriculation[index], index);
            if (!fd.immatriculation || !fd.immatriculation.trim())
                newErrors[index].immatriculation = "L'immatriculation est requise";
            if (existImmatriculation[index])
                newErrors[index].immatriculation =
                    "Un engin est déjà enregistré avec cette immatriculation";
            if (!fd.marque) newErrors[index].marque = "La marque est requise";
            if (!fd.nombrePlace)
                newErrors[index].nombrePlace = "Le nombre de places est requis";
            if (!fd.carosserie)
                newErrors[index].carosserie = "La carosserie est requise";
            if (!fd.moteur) newErrors[index].moteur = "Le moteur est requis";
            if (!fd.genre) newErrors[index].genre = "Le genre du moteur est requis";
            if (!fd.type) newErrors[index].type = "Le type de moteur est requis";
            if (!fd.dateMiseEnCirculation)
                newErrors[index].dateMiseEnCirculation =
                    "La date de mise en circulation est requise";
            if (!fd.numeroChassis)
                newErrors[index].numeroChassis = "Le numéro de châssis est requis";
            if (fd.numeroChassis?.length !== 17)
                newErrors[index].numeroChassis =
                    "Le numéro de châssis doit comporter 17 caractères";
            if (existChassis[index])
                newErrors[index].numeroChassis =
                    "Un moteur est déjà enregistré avec ce numéro de châssis!";
        });

        // Add more validation rules if needed
        setErrors(newErrors);

        let valid = true;
        newErrors.forEach((ne) => {
            if (Object.keys(ne).length !== 0) {
                valid = false;
            }
        });

        return valid;
    };

    const onSubmit = (e) => {
        e.preventDefault();
    };

    const disabledCirculationDate = (current) => {
        // Can not select years after this year
        return current && current.year() > dayjs().year();
    };

    const handleGarantiesChange = (value, index) => {
        let newGaranties = [...garanties];
        newGaranties[index] = value;
        setGaranties(newGaranties);
    };

    const handleClose = () => setShowAddPersonModal(false);

    const handleSousGarantie = (index, event) => {
        let newFormValues = [...formValues];
        // console.log("EVENT: ", event.target.value);
        var gtys = [];
        event.forEach((e) => {
            let g = ncGuaranties.find((el) => el.codeId === e);
            gtys.push(g);
        });
        // console.log("GTY: ", gtys);
        axiosInstance
            .post(`/api/sous-garantie/garanties`, JSON.stringify(gtys))
            .then((ress) => {
                newFormValues[index]["sousGaranties"] = ress.data;
                setFormValues(newFormValues);
            })
            .catch((errr) => {
                console.log(errr);
            });
    };

    const handleAssureChange = (e, index) => {
        let newFormValues = [...formValues];

        const { name, value } = e.target;

        newFormValues[index].personne[name] = value;

        setFormValues(newFormValues);
    };

    // Pont vers handleChange (qui attend un event DOM et lit e.target.id / name / value) pour
    // brancher les composants Ant Design (dont l'onChange renvoie une VALEUR, pas un event).
    const setField = (id, value, index = 0) =>
        handleChange({ target: { id, name: id, value } }, id, index);

    const formatCurrency = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

    // Query string commune (date d'effet + BNS) pour le preview ET la validation, afin que
    // l'aperçu et le montant réellement appliqué reposent sur EXACTEMENT les mêmes paramètres.
    // Le BNS n'est transmis que si l'utilisateur habilité (HCO080) l'a édité ; sinon le backend
    // reprend automatiquement le BNS du contrat en cours.
    const buildCvQueryString = () => {
        const params = new URLSearchParams();
        if (dateEffet) params.append("dateEffet", dayjs(dateEffet).format("YYYY-MM-DD"));
        if (canEditBonus && bonusMalusEdite != null && bonusMalusEdite !== "") {
            params.append("bonusMalus", Number(bonusMalusEdite));
        }
        // Frais accessoires édités : transmis SEULEMENT si l'utilisateur habilité (HCO079) les a
        // saisis ; sinon le backend applique le barème par défaut. Même canal preview + validation.
        if (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "") {
            params.append("accessoires", Number(accessoiresEdites));
        }
        return params.toString() ? `?${params.toString()}` : "";
    };

    // Calcul du preview financier côté backend. Le changement de véhicule conserve la
    // couverture du véhicule remplacé : le preview ne dépend donc que de l'engin sélectionné.
    const calculatePreview = async () => {
        try {
            if (!selectedEngin || !contrat?.codeId) {
                setPreview(null);
                return;
            }
            const oldEngin = JSON.parse(selectedEngin);
            const response = await axiosInstance.put(
                `/api/engin/preview-changement-vehicule/codeId/${contrat.codeId}/${oldEngin.codeId}${buildCvQueryString()}`,
                JSON.stringify(formValues[0])
            );
            setPreview(response.data);
        } catch (e) {
            console.error("Erreur lors du calcul du preview du changement de véhicule:", e);
            // On CONSERVE le dernier récap valide : une erreur transitoire (frappe en cours,
            // requête concurrente) ne doit pas faire disparaître le récapitulatif déjà affiché.
        }
    };

    useEffect(() => {
        if (selectedEngin && contrat?.codeId) {
            calculatePreview();
        } else {
            setPreview(null);
        }
        // Le preview ne dépend que des paramètres tarifaires du nouvel engin (puissance/volume,
        // valeur), de la date d'effet (prorata) et du BNS — pas de chaque frappe (immat, moteur…).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEngin, contrat?.codeId, dateEffet, bonusMalusEdite, accessoiresEdites,
        formValues[0]?.puissance, formValues[0]?.volume, formValues[0]?.valeurDeclaree]);

    // Affichage d'une erreur de validation sous un champ (validation manuelle via l'état `errors`).
    const errTxt = (k) =>
        errors[0]?.[k] ? (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 2 }}>{errors[0][k]}</div>
        ) : null;

    const isMoto = contrat?.engins?.[0]?.categorie?.nom === "MOTO";

    // Rendre les dropdowns dans le <body> plutôt que dans le wrapper du SDIModal (qui a un
    // `transform` pour le drag). Ce transform faussait le calcul de scroll de la liste
    // virtualisée d'AntD -> bug "une seule marque qui se répète". En sortant du conteneur
    // transformé, on peut GARDER la virtualisation (donc les perfs) sans le glitch.
    const popupToBody = () => document.body;

    // Options memoïsées : sinon elles sont reconstruites à CHAQUE frappe (chaque render),
    // ce qui, sur des listes longues, ralentit énormément la saisie.
    const carosserieOptions = useMemo(
        () => (carosseries || []).map((car) => ({ label: car.libelleParametre, value: car.libelleParametre })),
        [carosseries]
    );
    const genreOptions = useMemo(
        () => (genres || []).map((g) => ({ label: g.libelleParametre, value: g.libelleParametre })),
        [genres]
    );
    const concessionnaireOptions = useMemo(
        () => (concessionnaires || []).map((con) => ({ label: con.label, value: con.value })),
        [concessionnaires]
    );

    // Bouton principal : étape 0 → on valide la saisie puis on passe à la confirmation ;
    // étape 1 → on soumet réellement l'avenant.
    const handleModalOk = () => {
        if (currentStep === 0) {
            if (!selectedEngin) {
                toast("Sélectionnez le véhicule à remplacer");
                return;
            }
            if (!validateForm()) {
                return;
            }
            setCurrentStep(1);
        } else {
            handleChangementVehiculeSubmit();
        }
    };

    // Bouton secondaire : étape 1 → revenir à la saisie ; étape 0 → fermer la modale.
    const handleModalCancel = () => {
        if (currentStep === 1) {
            setCurrentStep(0);
        } else {
            handleCancel();
        }
    };
    const ncGuarantieOptions = useMemo(
        () => (ncGuaranties || []).map((ng) => ({ label: ng.nom, value: ng.codeId })),
        [ncGuaranties]
    );

    return (
        <SDIModal
            title="CHANGEMENT DE VEHICULE"
            confirmLoading={loading}
            centered
            open={modalChangementVehicule}
            onOk={() => handleModalOk()}
            onCancel={() => handleModalCancel()}
            okText={currentStep === 0 ? "Suivant →" : "Valider le changement"}
            cancelText={currentStep === 1 ? "← Précédent" : "Annuler"}
            okButtonProps={{ disabled: currentStep === 0 && !selectedEngin }}
            width={900}
            zIndex={1500}
        >
            <Steps
                size="small"
                current={currentStep}
                style={{ marginBottom: 16 }}
                items={[
                    { title: "Saisie du nouveau véhicule" },
                    { title: "Confirmation" },
                ]}
            />
            {/* ÉTAPE 0 — saisie. Masquée (et non démontée, pour préserver l'état) à l'étape 1. */}
            <div style={{ display: currentStep === 0 ? "block" : "none" }}>
            <AvenantAgencySelector
                organismeCode={props?.contrat?.organisme?.code}
                defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                defaultIntermediaire={props?.contrat?.intermediaire}
                categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
            />
            <Card
                size="small"
                title={<Typography.Text strong style={{ color: "#242166" }}>🗓️ Paramètres de l'avenant</Typography.Text>}
                style={{ marginBottom: 16, borderColor: "#2421661f" }}
                headStyle={{ background: "#f5f5ff" }}
                bodyStyle={{ padding: 16 }}
            >
                <Row gutter={[16, 14]}>
                    <Col xs={24} md={12}>
                        <label className="form-label">Date d'effet</label>
                        <DatePicker
                            value={dateEffet}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            getPopupContainer={popupToBody}
                            onChange={(d) => setDateEffet(d || dayjs())}
                            style={{ width: "100%" }}
                        />
                        <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }}>
                            Détermine le prorata (période restante jusqu'à l'échéance).
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <label className="form-label">Bonus / Malus (BNS)</label>
                        <InputNumber
                            value={canEditBonus && bonusMalusEdite != null ? bonusMalusEdite
                                : (preview?.bonusMalus ?? 0)}
                            onChange={(v) => setBonusMalusEdite(v)}
                            disabled={!canEditBonus}
                            addonAfter="%"
                            style={{ width: "100%" }}
                        />
                        <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }}>
                            {canEditBonus
                                ? "Repris du contrat en cours — modifiable."
                                : "Repris du contrat en cours (édition réservée : HCO080)."}
                        </div>
                    </Col>
                </Row>
            </Card>
            <Card
                size="small"
                title={<Typography.Text strong style={{ color: "#242166" }}>🚗 Véhicule à remplacer</Typography.Text>}
                style={{ marginBottom: 16, borderColor: "#2421661f" }}
                headStyle={{ background: "#f5f5ff" }}
                bodyStyle={{ padding: 16 }}
            >
                <label className="form-label" htmlFor="vehiculeAChanger">Véhicule à changer</label>
                <Select
                    showSearch
                    getPopupContainer={popupToBody}
                    optionFilterProp="label"
                    placeholder="Sélectionnez le véhicule à remplacer"
                    value={selectedEngin}
                    onChange={(value) => {
                        setSelectedEngin(value);
                    }}
                    // Ant Design attend `label` (pas `libelle`) → sinon les options s'affichent vides.
                    options={contrat?.engins?.map((engin, index) => (
                        {
                            label: `${engin.immatriculation || "Sans immatriculation"} — ${engin.marque || ""}`
                                + (engin.numeroChassis ? ` (Châssis ${engin.numeroChassis})` : ""),
                            value: JSON.stringify(engin),
                            key: index
                        }

                    ))}
                    style={{ width: "100%" }}
                />
                {(() => {
                    let eng = null;
                    try { eng = selectedEngin ? JSON.parse(selectedEngin) : null; } catch (e) { eng = null; }
                    if (!eng) return null;
                    return (
                        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                            <Tag color="blue">Immatriculation : {eng.immatriculation || "-"}</Tag>
                            <Tag>Marque : {eng.marque || "-"}</Tag>
                            <Tag>Châssis : {eng.numeroChassis || "-"}</Tag>
                            {eng.genre ? <Tag>Genre : {eng.genre}</Tag> : null}
                            {eng.puissance ? <Tag>Puissance/Volume : {eng.puissance || eng.volume}</Tag> : null}
                            {eng.categorie?.nom ? <Tag>Catégorie : {eng.categorie?.nom}</Tag> : null}
                        </div>
                    );
                })()}
            </Card>
            <Card
                size="small"
                title={<Typography.Text strong style={{ color: "#242166" }}>🆕 Informations du nouveau véhicule</Typography.Text>}
                style={{ marginBottom: 8, borderColor: "#2421661f" }}
                headStyle={{ background: "#f5f5ff" }}
                bodyStyle={{ padding: 16 }}
            >
                <Row gutter={[16, 14]}>
                    {/* Immatriculation (champ unique) */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Immatriculation</label>
                        <Input
                            placeholder="Ex : AB 1234 TG"
                            value={formValues[0].immatriculation}
                            status={errors[0]?.immatriculation ? "error" : ""}
                            onChange={(e) => setField("immatriculation", e.target.value)}
                            style={{ width: "100%" }}
                        />
                        {errTxt("immatriculation")}
                    </Col>

                    {/* Marque */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Marque</label>
                        <MarqueSelect
                            marques={marques}
                            getPopupContainer={popupToBody}
                            value={formValues[0].marque}
                            status={errors[0]?.marque ? "error" : ""}
                            onChange={(val) => { setField("marque", val); setField("type", ""); }}
                            style={{ width: "100%" }}
                        />
                        {errTxt("marque")}
                    </Col>

                    {/* Énergie */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Énergie</label>
                        <Select
                            value={formValues[0].energie}
                            onChange={(val) => setField("energie", val)}
                            style={{ width: "100%" }}
                            options={[{ label: "ESSENCE", value: "ESSENCE" }, { label: "DIESEL", value: "DIESEL" }, { label: "ELECTRIQUE", value: "ELECTRIQUE" }]}
                        />
                    </Col>

                    {/* Puissance / Volume (selon catégorie) */}
                    {!isMoto ? (
                        <Col xs={24} md={12}>
                            <label className="form-label">Puissance (en CV)</label>
                            <InputNumber
                                style={{ width: "100%" }}
                                placeholder="Puissance"
                                value={formValues[0].puissance}
                                onChange={(val) => { setField("puissance", val); handlePuissance(val, 0); }}
                            />
                        </Col>
                    ) : (
                        <Col xs={24} md={12}>
                            <label className="form-label">Volume (en cm³)</label>
                            <InputNumber
                                style={{ width: "100%" }}
                                placeholder="Volume"
                                value={formValues[0].volume}
                                onChange={(val) => { setField("volume", val); handlePuissance(val, 0); }}
                            />
                        </Col>
                    )}

                    {/* Nombre de places */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Nombre de places</label>
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Nbre de places"
                            value={formValues[0].nombrePlace}
                            status={errors[0]?.nombrePlace ? "error" : ""}
                            onChange={(val) => setField("nombrePlace", val)}
                        />
                        {errTxt("nombrePlace")}
                    </Col>

                    {/* Usage */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Usage</label>
                        <Select
                            value={formValues[0].usage}
                            onChange={(val) => setField("usage", val)}
                            style={{ width: "100%" }}
                            options={[{ label: "Taxi", value: "TAXI" }, { label: "Privé", value: "PRIVE" }]}
                        />
                    </Col>

                    {/* Carrosserie */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Carrosserie</label>
                        <Select
                            showSearch
                            getPopupContainer={popupToBody}
                            optionFilterProp="label"
                            placeholder="Sélectionner une carrosserie"
                            value={formValues[0].carosserie || undefined}
                            status={errors[0]?.carosserie ? "error" : ""}
                            onChange={(val) => setField("carosserie", val)}
                            style={{ width: "100%" }}
                            options={carosserieOptions}
                        />
                        {errTxt("carosserie")}
                    </Col>

                    {/* Moteur */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Moteur</label>
                        <Input
                            placeholder="Moteur"
                            value={formValues[0].moteur}
                            status={errors[0]?.moteur ? "error" : ""}
                            onChange={(e) => setField("moteur", e.target.value)}
                        />
                        {errTxt("moteur")}
                    </Col>

                    {/* Type (modèle) — dépend de la marque */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Type</label>
                        <TypeAutoComplete
                            marques={marques}
                            marque={formValues[0].marque}
                            value={formValues[0].type}
                            status={errors[0]?.type ? "error" : ""}
                            onChange={(val) => setField("type", val)}
                            getPopupContainer={popupToBody}
                            style={{ width: "100%" }}
                        />
                        {errTxt("type")}
                    </Col>

                    {/* Genre du moteur (MOTO uniquement) */}
                    {isMoto ? (
                        <Col xs={24} md={12}>
                            <label className="form-label">Genre du moteur</label>
                            <Select
                                showSearch
                                getPopupContainer={popupToBody}
                                optionFilterProp="label"
                                placeholder="Sélectionner un genre"
                                value={formValues[0].genre || undefined}
                                status={errors[0]?.genre ? "error" : ""}
                                onChange={(val) => setField("genre", val)}
                                style={{ width: "100%" }}
                                options={genreOptions}
                            />
                            {errTxt("genre")}
                        </Col>
                    ) : null}

                    {/* Concessionnaire */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Concessionnaire / Intermédiaire</label>
                        <Select
                            showSearch
                            getPopupContainer={popupToBody}
                            optionFilterProp="label"
                            placeholder="Veuillez sélectionner le concessionnaire"
                            value={formValues[0].concessionnaire || undefined}
                            onChange={(val) => setField("concessionnaire", val)}
                            style={{ width: "100%" }}
                            options={concessionnaireOptions}
                        />
                    </Col>

                    {/* Numéro de châssis */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Numéro de châssis</label>
                        <Input
                            placeholder="Numéro de châssis"
                            value={formValues[0].numeroChassis}
                            status={errors[0]?.numeroChassis ? "error" : ""}
                            onChange={(e) => setField("numeroChassis", e.target.value)}
                        />
                        {errTxt("numeroChassis")}
                    </Col>

                    {/* Valeur vénale (met aussi à jour valeur à neuf / déclarée via handleChange) */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Valeur vénale</label>
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Valeur vénale"
                            value={formValues[0].valeurVenale}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                            parser={(v) => v.replace(/\s/g, "")}
                            onChange={(val) => setField("valeurVenale", val)}
                        />
                    </Col>

                    {/* Valeur à neuf */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Valeur à neuf</label>
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Valeur à neuf"
                            value={formValues[0].valeurANeuf}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                            parser={(v) => v.replace(/\s/g, "")}
                            onChange={(val) => setField("valeurANeuf", val)}
                        />
                    </Col>

                    {/* Valeur déclarée */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Valeur déclarée</label>
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Valeur déclarée"
                            value={formValues[0].valeurDeclaree}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                            parser={(v) => v.replace(/\s/g, "")}
                            onChange={(val) => setField("valeurDeclaree", val)}
                        />
                    </Col>

                    {/* Date de mise en circulation */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Date de mise en circulation</label>
                        <DatePicker
                            onChange={(e) => handleDateMiseEnCirculation(e, 0)}
                            picker="year"
                            placeholder="Veuillez sélectionner l'année"
                            disabledDate={disabledCirculationDate}
                            showNow
                            style={{ width: "100%", borderColor: errors[0]?.dateMiseEnCirculation ? "red" : undefined }}
                        />
                        {errTxt("dateMiseEnCirculation")}
                    </Col>

                    {/* Garanties supplémentaires */}
                    <Col xs={24} md={12}>
                        <label className="form-label">Garanties supplémentaires</label>
                        <Select
                            mode="multiple"
                            getPopupContainer={popupToBody}
                            optionFilterProp="label"
                            style={{ width: "100%" }}
                            placeholder="Sélectionnez les garanties"
                            onChange={(e) => { handleGarantiesChange(e, 0); handleSousGarantie(0, e); }}
                            options={ncGuarantieOptions}
                        />
                    </Col>
                </Row>

                {/* Pièce jointe + assuré */}
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
                    <Upload {...prop(0)}>
                        <Button style={{ background: "#595959", color: "white" }} icon={<CreditCardOutlined />}>
                            Joindre la carte grise
                        </Button>
                    </Upload>
                    <Button type="dashed" icon={<UserAddOutlined />} onClick={() => handleShowAddPersonModal(0)}>
                        Préciser l'assuré (si différent du souscripteur)
                    </Button>
                </div>
            </Card>
            </div>
            {/* Fin ÉTAPE 0 */}

            {/* ÉTAPE 1 — confirmation : rappel de l'opération avant validation. */}
            {currentStep === 1 && (() => {
                let oldEng = null;
                try { oldEng = selectedEngin ? JSON.parse(selectedEngin) : null; } catch (e) { oldEng = null; }
                const nv = formValues[0] || {};
                // BNS réellement appliqué : la valeur éditée (si habilité) prime sur celle du preview.
                const effectiveBns = (canEditBonus && bonusMalusEdite != null && bonusMalusEdite !== "")
                    ? Number(bonusMalusEdite)
                    : Number(preview?.bonusMalus ?? 0);
                return (
                    <Card
                        size="small"
                        title={<Typography.Text strong style={{ color: "#242166" }}>✅ Confirmation du changement</Typography.Text>}
                        style={{ marginBottom: 8, borderColor: "#2421661f" }}
                        headStyle={{ background: "#f5f5ff" }}
                        bodyStyle={{ padding: 16 }}
                    >
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Vérifiez les informations ci-dessous avant de valider. L'avenant partira en proposition ; le règlement sera généré à la validation."
                        />
                        <Row gutter={[16, 8]}>
                            <Col xs={24} md={12}>
                                <Typography.Text type="secondary">Véhicule remplacé</Typography.Text>
                                <div><Typography.Text strong>{oldEng?.immatriculation || "-"}</Typography.Text>{" "}
                                    {oldEng?.marque ? `— ${oldEng.marque}` : ""}</div>
                                <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                                    Puissance/Volume : {oldEng?.volume || oldEng?.puissance || "-"}
                                </div>
                            </Col>
                            <Col xs={24} md={12}>
                                <Typography.Text type="secondary">Nouveau véhicule</Typography.Text>
                                <div><Typography.Text strong>{nv.immatriculation || "-"}</Typography.Text>{" "}
                                    {nv.marque ? `— ${nv.marque}` : ""}</div>
                                <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                                    Puissance/Volume : {nv.volume || nv.puissance || "-"}
                                </div>
                            </Col>
                        </Row>
                        <Divider style={{ margin: "10px 0" }} />
                        <Row gutter={[16, 8]}>
                            <Col xs={24} md={12}>
                                <Tag color="blue">Date d'effet : {dayjs(dateEffet).format("DD/MM/YYYY")}</Tag>
                            </Col>
                            <Col xs={24} md={12}>
                                <Tag color={effectiveBns < 0 ? "red" : "green"}>
                                    BNS : {effectiveBns} %
                                    {" "}({effectiveBns < 0 ? "Malus" : "Bonus"})
                                </Tag>
                            </Col>
                        </Row>
                        {!preview && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: 12 }}
                                message="Le récapitulatif financier n'a pas pu être calculé (aperçu indisponible). Revenez à l'étape précédente et vérifiez la puissance / la valeur du nouveau véhicule, puis réessayez."
                            />
                        )}
                    </Card>
                );
            })()}

            {/* Récapitulatif financier (preview backend) — UNIQUEMENT à l'étape 2 (confirmation). */}
            {currentStep === 1 && preview && (
                <Card size="small" style={{ marginBottom: 8, borderColor: "#2421661f", background: "#fafaff" }}>
                    <Typography.Title level={5} style={{ marginTop: 0, color: "#242166" }}>
                        Récapitulatif financier
                    </Typography.Title>

                    {/* Détail par garantie : prime annuelle (avant prorata) ancienne vs nouvelle.
                        Rend visible l'origine de l'écart — ou son absence (ex. RC forfaitaire d'une
                        catégorie : même prime quelle que soit la puissance → ancienne = nouvelle). */}
                    {Array.isArray(preview.cvLignes) && preview.cvLignes.length > 0 && (
                        <div style={{ overflowX: "auto", marginBottom: 10 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eee", color: "#8c8c8c" }}>
                                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Garantie</th>
                                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Ancien engin</th>
                                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Nouvel engin</th>
                                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Écart</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.cvLignes.map((l, i) => {
                                        const ecart = Number(l.nouvelle || 0) - Number(l.ancienne || 0);
                                        return (
                                            <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                <td style={{ textAlign: "left", padding: "4px 6px" }}>{l.nom}</td>
                                                <td style={{ textAlign: "right", padding: "4px 6px" }}>{formatCurrency(l.ancienne)}</td>
                                                <td style={{ textAlign: "right", padding: "4px 6px" }}>{formatCurrency(l.nouvelle)}</td>
                                                <td style={{ textAlign: "right", padding: "4px 6px", color: ecart === 0 ? "#8c8c8c" : (ecart < 0 ? "#d46b08" : "#389e0d") }}>
                                                    {ecart > 0 ? "+" : ""}{formatCurrency(ecart)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>
                                Primes annuelles (100 %). La prime nette de l'avenant applique ensuite le prorata (période restante) à l'écart total.
                            </div>
                        </div>
                    )}

                    <Row justify="space-between" style={{ marginBottom: 6 }}>
                        <Col><Typography.Text>Prime nette (écart × prorata) :</Typography.Text></Col>
                        <Col><Typography.Text strong>{formatCurrency(preview.primeNette)}</Typography.Text></Col>
                    </Row>
                    <Row justify="space-between" align="top" style={{ marginBottom: 6 }}>
                        <Col><Typography.Text>Frais accessoires :</Typography.Text></Col>
                        <Col style={{ textAlign: "right" }}>
                            {canEditAccessoires ? (
                                <>
                                    <InputNumber
                                        size="small"
                                        min={0}
                                        style={{ width: 150 }}
                                        value={accessoiresEdites != null ? accessoiresEdites : preview.accessoires}
                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                        parser={(v) => v.replace(/\s/g, "")}
                                        onChange={(v) => setAccessoiresEdites(v)}
                                    />
                                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                                        Par défaut : {formatCurrency(preview.accessoiresDefaut != null ? preview.accessoiresDefaut : preview.accessoires)}
                                    </div>
                                </>
                            ) : (
                                <Typography.Text>{formatCurrency(preview.accessoires)}</Typography.Text>
                            )}
                        </Col>
                    </Row>
                    <Row justify="space-between" style={{ marginBottom: 6 }}>
                        <Col><Typography.Text>Taxes (6%) :</Typography.Text></Col>
                        <Col><Typography.Text>{formatCurrency(preview.taxes)}</Typography.Text></Col>
                    </Row>
                    <Divider style={{ margin: "8px 0" }} />
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Typography.Text strong>
                                {Number(preview.primeNette) < 0 ? "RISTOURNE À RESTITUER :" : "PRIME TOTALE À PAYER :"}
                            </Typography.Text>{" "}
                            <Tag color={Number(preview.primeNette) < 0 ? "orange" : "green"}>
                                {Number(preview.primeNette) < 0 ? "Ristourne" : "À payer"}
                            </Tag>
                        </Col>
                        <Col>
                            <Typography.Text strong style={{ color: Number(preview.primeNette) < 0 ? "#d46b08" : "#1677ff" }}>
                                {formatCurrency(preview.primeTotale)}
                            </Typography.Text>
                        </Col>
                    </Row>
                    <div style={{ marginTop: 8 }}>
                        <Tag color="blue">Le changement de véhicule conserve la couverture du véhicule remplacé (sans surprime).</Tag>
                    </div>
                </Card>
            )}

            <SDIModal
                title="Ajouter un assuré"
                centered
                open={modalAssureOpen}
                onOk={() => setModalAssureOpen(false)}
                onCancel={() => setModalAssureOpen(false)}
            >
                <div className="container" justify="center">
                    <div className="text-center mb-6">
                        <h4 className="address-title mb-2">Ajouter une personne</h4>
                        <p className="address-subtitle">
                            Préciser l'assuré, si différent du souscripteur
                        </p>
                    </div>
                    <form id="addNewAddressForm" className="row g-12" onSubmit={onSubmit}>
                        <div className="col-12 col-md-12">
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="nom">
                                        Nom
                                    </label>
                                    <input
                                        type="text"
                                        id="nom"
                                        name="nom"
                                        class="form-control"
                                        placeholder="Entrez le nom de l'assuré"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="prenom">
                                        Prénom(s)
                                    </label>
                                    <input
                                        type="text"
                                        id="prenom"
                                        name="prenom"
                                        class="form-control"
                                        placeholder="Entrez le prénom de l'assuré"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="genre">
                                        Genre
                                    </label>
                                    <select
                                        id="genre"
                                        name="genre"
                                        class="form-control"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    >
                                        <option value="MASCULIN">MASCULIN</option>
                                        <option value="FEMININ">FEMININ</option>
                                    </select>
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="profession">
                                        Profession
                                    </label>
                                    <select
                                        id="profession"
                                        name="profession"
                                        class="form-control"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    >
                                        <option value="">Selectionnez la profession</option>
                                        {professions?.map((prof, id) => (
                                            <option key={id} value={prof.libelleParametre}>
                                                {prof.libelleParametre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="email">
                                        Email
                                    </label>
                                    <input
                                        type="text"
                                        id="email"
                                        name="email"
                                        class="form-control"
                                        placeholder="Entrez l'adresse mail"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="adresse">
                                        Adresse de résidence
                                    </label>
                                    <input
                                        type="text"
                                        id="adresse"
                                        name="adresse"
                                        class="form-control"
                                        placeholder="Entrez l'adresse de résidence"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="phone">
                                        Numéro de téléphone
                                    </label>
                                    <div class="input-group">
                                        <span class="input-group-text">TG (+228)</span>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            class="form-control phone-number-mask"
                                            placeholder="00000000"
                                            onChange={(e) => handleAssureChange(e, personneId)}
                                        />
                                    </div>
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="typePiece">
                                        Type de pièce
                                    </label>
                                    <select
                                        id="typePiece"
                                        name="typePiece"
                                        class="form-control"
                                        data-allow-clear="true"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    >
                                        <option value="CNI">CNI</option>
                                        <option value="PASSEPORT">PASSEPORT</option>
                                    </select>
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="form-label" for="numIdentite">
                                        Numéro de la pièce d'identité
                                    </label>
                                    <input
                                        type="text"
                                        id="numIdentite"
                                        name="numIdentite"
                                        class="form-control"
                                        placeholder="Entrez le numéro de la pièce"
                                        onChange={(e) => handleAssureChange(e, personneId)}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label" htmlFor="dateDelivrance">
                                        Date de délivrance
                                    </label>
                                    <div class="col-md-12">
                                        <input
                                            class="form-control"
                                            type="month"
                                            id="dateDelivrance"
                                            name="dateDelivrance"
                                            onChange={(e) => handleAssureChange(e, personneId)}
                                        />
                                    </div>
                                </div>
                                <div class="col-12 text-center">
                                    <br />
                                    <button type="submit" class="btn btn-primary me-3">
                                        Enregistrer
                                    </button>
                                    <button
                                        type="reset"
                                        class="btn btn-label-secondary"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => handleClose()}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </SDIModal>
        </SDIModal>
    );
};

ChangementDeVehicule.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default ChangementDeVehicule;